import type { ProductDto } from "@/api/types";
import type { ProvinceFull } from "./province";

export interface OrderShippingDetails {
  shippingBusinessName: string;
  shippingStreetAddress: string;
  shippingOptionalUnit: string;
  shippingCity: string;
  shippingProvince: ProvinceFull | "";
  shippingPostalCode: string;
  products: OrderProduct[];
  ada: string;
}

export interface OrderExtraCosts {
  shippingCost: number;
  tax: number;
}

export enum OrderPurchaseType {
  Credit = 0,
  PurchaseOrder = 1,
  Account = 2,
}

export type OrderFlow = "standard" | "ontario" | "emergent" | "indigenous";

export interface OrderProduct {
  id: number;
  quantity: number;
}

export interface OrderDetails extends OrderShippingDetails {
  id: number;
  phoneNumber: string;
  organizationType?: string;
  organizationTypeOther?: string;
  emailAddress: string;
  confirmEmailAddress: string;
  subtotal: number;
  shippingCost: number;
  tax: number;
  total: number;
  billingName: string;
  billingBusinessName: string;
  billingStreetAddress: string;
  billingOptionalUnit: string;
  billingCity: string;
  billingProvince: ProvinceFull | "";
  billingPostalCode: string;
  billingAllowPo: boolean;
  sameAsBilling: boolean;
  shippingName: string;
  orderReference?: string;
  language: string;
  subscribeToEmailUpdates: boolean;
  isActive: boolean;
  purchaseType: OrderPurchaseType;
  billingAddressId?: number;
  shippingAddressId?: number;
  saveBillingAddress: boolean;
  saveShippingAddress: boolean;
  captchaToken?: string;
  promotionalCode?: string;
  promotionalDiscountAmount?: number;
  isManualAddress?: boolean;
}

export const createDefaultOrderDetails = (): OrderDetails => {
  return {
    id: 0,
    ada: "",
    phoneNumber: "",
    emailAddress: "",
    confirmEmailAddress: "",
    products: [],
    subtotal: 0,
    shippingCost: 0,
    tax: 0,
    total: 0,
    billingName: "",
    billingBusinessName: "",
    billingStreetAddress: "",
    billingOptionalUnit: "",
    billingCity: "",
    billingProvince: "",
    billingPostalCode: "",
    billingAllowPo: false,
    sameAsBilling: false,
    shippingName: "",
    shippingBusinessName: "",
    shippingStreetAddress: "",
    shippingOptionalUnit: "",
    shippingCity: "",
    shippingProvince: "",
    shippingPostalCode: "",
    orderReference: "",
    language: "english",
    isActive: false,
    subscribeToEmailUpdates: false,
    purchaseType: OrderPurchaseType.Credit,
    saveBillingAddress: false,
    saveShippingAddress: false,
  };
};

export interface OrderCheckboxData {
  agreeToPrivacyPolicy: boolean;
  agreeToTerms: boolean;
  confirmAge: boolean;
  agreeToTraining: boolean;
  agreeToShareMyData: boolean;
  agreeToFairUse: boolean;
}

export const createOrderCheckboxData = () =>
  ({
    agreeToPrivacyPolicy: false,
    agreeToTerms: false,
    confirmAge: false,
    agreeToTraining: false,
    agreeToShareMyData: false,
    agreeToFairUse: false,
  }) as OrderCheckboxData;

export interface OrderProductData extends ProductDto {
  quantity: number;
  total: number;
  totalFormatted: string;
  priceFormatted: string;

  language: {
    numberOfLabel: string;
    pricePerLabel: string;
    itemsDescLabel: string;
    items: string[];
    minimumOrder: string;
    imageSrc: string;
  };
}
